import { useParams } from 'react-router-dom'
import { useState } from 'react'
import SuspendModal from '../../components/admin/SuspendModal'
import USER_TYPE from '../../constants/userType'
import DocumentListing from '../../components/common/DocumentListing'
import { formatTime } from '../../utils/date.util'
import { useShopDetails } from '../../hooks/useShopDetails'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ShopActionButtons from '../../components/admin/Shops/ShopActionButtons'
import DisplayActiveSuspended from '../../components/admin/DisplayActiveSuspended'
import InfoGrid from '../../components/common/InfoGrid'
import UserTypeDisplay from '../../components/common/UserTypeDisplay'
import SubscriptionDetails from '../../components/common/SubscriptionDetails'
import DisplayShopName from '../../components/common/DisplayShopName'

export default function RetailerDetailsPage() {
  const { shopSlug } = useParams()
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false)
  const { shop, isLoading } = useShopDetails(USER_TYPE.retailer, shopSlug)

  if (isLoading) return <LoadingSpinner />

  const onCloseSuspendModal = () => setIsSuspendModalOpen(false)

  const {
    user,
    shopName,
    buildingName,
    floor,
    shopNumber,
    createdAt,
    isApproved,
    violationReports,
    activeTickets,
    userSubscription,
  } = shop

  const hasDocuments = Array.isArray(shop?.shopDocuments) && shop.shopDocuments.length > 0

  const renderStatus = () => {
    return (
      <>
        <DisplayActiveSuspended isActive={user?.isActive} suspendedBy={user?.suspendedBy} />
        {!user?.isActive && user?.suspendedAt && (
          <span className="text-foreground-secondary text-sm block mt-1">
            {`Suspended at: ${formatTime(user?.suspendedAt)}`} by {user?.suspender?.name}
          </span>
        )}

        {!user?.isActive && user?.suspensionReason && (
          <span className="text-foreground-secondary text-sm block mt-1">
            {`Reason: ${user?.suspensionReason}`}
          </span>
        )}
      </>
    )
  }

  const retailerInfoItems = [
    { label: 'Name', value: user?.name },
    { label: 'Phone Number', value: user?.phoneNumber },
    {
      label: 'Status',
      value: renderStatus(),
    },

    { label: 'User Type', value: <UserTypeDisplay userType={user?.userType} /> },
    { label: 'Created', value: formatTime(user?.createdAt) },
  ]

  const shopInfoItems = [
    {
      label: 'Shop Name',
      value: (
        <DisplayShopName
          shop={{
            name: shopName,
            isPremium: shop.isPremium,
            isVerified: shop.isApproved,
          }}
        />
      ),
    },
    { label: 'Building Name', value: buildingName },
    { label: 'Floor', value: floor },
    { label: 'Shop Number', value: shopNumber },
    {
      label: 'Verification',
      value: isApproved ? 'Verified' : 'Not Verified',
      className: isApproved ? 'text-success-500' : 'text-error-700',
    },
    ...(shop.rejectedBy
      ? [
          { label: 'Rejected By', value: shop?.rejectedBy ?? '—' },
          { label: 'Rejected At', value: formatTime(shop?.rejectedAt) ?? '—' },
          { label: 'Rejection Reason', value: shop?.rejectedReason },
        ]
      : []),
    {
      label: 'Documents',
      value: hasDocuments ? `${shop.shopDocuments.length} submitted` : 'Not submitted',
      className: hasDocuments ? 'text-success-500' : 'text-error-700',
    },
    { label: 'Tickets', value: activeTickets ?? 0 },
    { label: 'Violations', value: violationReports ?? 0 },
  ]

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold mb-4 text-foreground">Shop Info</h2>
          <InfoGrid infoItems={shopInfoItems} />
        </div>
        <div>
          <h2 className="text-lg font-semibold mb-4 text-foreground flex justify-between items-center">
            Retailer Info
            <ShopActionButtons shop={shop} onSuspendClick={() => setIsSuspendModalOpen(true)} />
          </h2>
          <InfoGrid infoItems={retailerInfoItems} />
        </div>
      </div>

      <SubscriptionDetails subscription={userSubscription} />
      <div className="w-full mt-8">
        <DocumentListing
          documents={shop?.shopDocuments}
          shopId={user?.id}
          userType={USER_TYPE.retailer}
          readOnly={true}
        />
      </div>

      <SuspendModal
        isOpen={isSuspendModalOpen}
        onClose={onCloseSuspendModal}
        shopSlug={shopSlug}
        user={{
          id: user?.id,
          name: shopName,
          buildingName,
          floor,
          shopNumber,
          createdAt,
          isActive: user?.isActive,
          userType: USER_TYPE.retailer,
        }}
      />
    </>
  )
}
